/**
 * Spanish landing page (/es) — Phase 2 with real hero carousel.
 *
 * Hero carousel mirrors the EN /'s 5-slide rotation, using the same image
 * assets (Phase 3d will swap in -es image variants if/when burned-in EN
 * text is replaced) but with native Spanish slide titles, subtitles and
 * CTAs that link to /es/* equivalents.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import EsSEO from '@/components/seo/EsSEO';
import BilingualForm from '@/components/BilingualForm';
import GoogleReviewsWidget from '@/components/common/GoogleReviewsWidget';
import InsuranceCarousel from '@/components/cards/InsuranceCarousel';
import useSpanishTrackingNumber from '@/hooks/useSpanishTrackingNumber';
import { ROUTE_MAP_EN_TO_ES } from '@/i18n/routeMap';

const SITE = 'https://fortworthdental.com';
const SCHEMA_PHONE = '(817) 985-3337';

const HOMEPAGE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Dentist',
  name: 'Fort Worth Dental',
  image: `${SITE}/logo-fwd.png`,
  url: `${SITE}/es`,
  telephone: SCHEMA_PHONE,
  address: {
    '@type': 'PostalAddress',
    streetAddress: '4620 Citylake Blvd W',
    addressLocality: 'Fort Worth',
    addressRegion: 'TX',
    postalCode: '76132',
    addressCountry: 'US',
  },
  areaServed: [
    { '@type': 'City', name: 'Fort Worth' },
    { '@type': 'City', name: 'Arlington' },
    { '@type': 'City', name: 'Benbrook' },
    { '@type': 'City', name: 'Forest Hill' },
    { '@type': 'City', name: 'White Settlement' },
    { '@type': 'City', name: 'Westover Hills' },
  ],
  inLanguage: 'es-US',
  knowsLanguage: ['English', 'Spanish'],
  availableLanguage: [{ '@type': 'Language', name: 'Spanish', alternateName: 'es' }],
  priceRange: '$$',
};

const HERO_SLIDES = [
  {
    title: 'Bienvenido a Fort Worth Dental',
    subtitle: 'Tu dentista familiar en Fort Worth — más de 35 años cuidando sonrisas',
    cta: 'Conoce a Nuestro Equipo',
    link: ROUTE_MAP_EN_TO_ES['/our-team'],
    image: '/images/services/banner-website-img-1.webp',
    imageFallback: '/images/services/banner-website-img-1.png',
    isTeamPhoto: true,
    badge: '⭐ Best of Fort Worth 2025',
  },
  {
    title: 'Carillas First Fit',
    subtitle: 'Una sonrisa nueva en solo 2 horas — la última tecnología en odontología cosmética',
    cta: 'Saber Más',
    link: ROUTE_MAP_EN_TO_ES['/first-fit-veneers'],
    image: 'https://images.unsplash.com/photo-1675526607070-f5cbd71dde92?w=1600&q=70&fit=crop&fm=webp',
    imageFallback: 'https://images.unsplash.com/photo-1675526607070-f5cbd71dde92?w=1600&q=70&fit=crop',
  },
  {
    title: 'Implantes Dentales',
    subtitle: 'El estándar moderno para reemplazar dientes perdidos — con apariencia 100% natural',
    cta: 'Consulta Gratis',
    link: ROUTE_MAP_EN_TO_ES['/dental-implants'],
    image: 'https://images.unsplash.com/photo-1663182234283-28941e7612da?w=1600&q=70&fit=crop&fm=webp',
    imageFallback: 'https://images.unsplash.com/photo-1663182234283-28941e7612da?w=1600&q=70&fit=crop',
  },
  {
    title: 'Invisalign®',
    subtitle: 'Endereza tus dientes discretamente — alineadores transparentes a la medida',
    cta: 'Empieza tu Sonrisa',
    link: ROUTE_MAP_EN_TO_ES['/invisalign'],
    image: 'https://images.unsplash.com/photo-1609840114035-3c981b782dfe?w=1600&q=70&fit=crop&fm=webp',
    imageFallback: 'https://images.unsplash.com/photo-1609840114035-3c981b782dfe?w=1600&q=70&fit=crop',
  },
  {
    title: 'Sonrisas Deslumbrantes',
    subtitle: 'Blanqueamiento dental profesional — hasta 8 tonos más blanco en una sola visita',
    cta: 'Llama Hoy',
    link: ROUTE_MAP_EN_TO_ES['/cosmetic-dentistry'],
    image: 'https://images.unsplash.com/photo-1654373535457-383a0a4d00f9?w=1600&q=70&fit=crop&fm=webp',
    imageFallback: 'https://images.unsplash.com/photo-1654373535457-383a0a4d00f9?w=1600&q=70&fit=crop',
  },
];

const SERVICES_FEATURED = [
  { es: 'Implantes Dentales', route: ROUTE_MAP_EN_TO_ES['/dental-implants'] },
  { es: 'Limpieza Dental', route: ROUTE_MAP_EN_TO_ES['/preventative-dentistry'] },
  { es: 'Frenos e Invisalign', route: ROUTE_MAP_EN_TO_ES['/invisalign'] },
  { es: 'Blanqueamiento Dental', route: ROUTE_MAP_EN_TO_ES['/teeth-whitening'] },
  { es: 'Endodoncia', route: ROUTE_MAP_EN_TO_ES['/root-canals'] },
  { es: 'Carillas de Porcelana', route: ROUTE_MAP_EN_TO_ES['/porcelain-veneers'] },
  { es: 'Coronas y Puentes', route: ROUTE_MAP_EN_TO_ES['/crowns-bridges'] },
  { es: 'Emergencia Dental', route: ROUTE_MAP_EN_TO_ES['/emergency-dentist'] },
];

// Mirrors EN homepage's "New Patient Welcome Offers" — 4 promo cards.
const WELCOME_OFFERS_ES = [
  {
    title: 'Implantes Dentales — Consulta Gratis con Radiografías',
    description: 'Fort Worth Dental ofrece CONSULTA GRATUITA + radiografías para nuevos pacientes de implantes dentales.',
    image: '/images/services/frame-1288-3.png',
    link: ROUTE_MAP_EN_TO_ES['/dental-implants-free-consult-with-x-rays'],
    cta: 'Ver Detalles',
  },
  {
    title: 'Invisalign® — Consulta Gratuita',
    description: 'Fort Worth Dental ofrece CONSULTA GRATUITA para Invisalign® — alineadores transparentes a la medida.',
    image: '/images/services/frame-1288-2.png',
    link: ROUTE_MAP_EN_TO_ES['/invisalign-free-consultation'],
    cta: 'Ver Detalles',
  },
  {
    title: '$199 Limpieza, Examen y Radiografías',
    description: '¿El secreto para evitar tratamientos dentales costosos y dolorosos? La prevención. Empieza con esta promoción.',
    image: '/images/services/frame-1288.png',
    link: ROUTE_MAP_EN_TO_ES['/179-healthy-cleaning-exam-and-set-of-x-rays'],
    cta: 'Ver Detalles',
  },
  {
    title: '$99 Examen de Emergencia y Radiografía',
    description: 'El mejor problema dental es el que nunca tuviste. Recibe atención de emergencia el mismo día cuando la necesites.',
    image: '/images/services/frame-1288-1.png',
    link: ROUTE_MAP_EN_TO_ES['/99-emergency-exam-and-x-ray-affordable-dental-services'],
    cta: 'Ver Detalles',
  },
];

// 4 actual doctors at Fort Worth Dental (matches EN /Home.jsx — Mund, Razo,
// Hiwiadi, Pham). Kept inline rather than reusing <DoctorCard> because that
// component hard-codes English button copy ("Read About …").
const DOCTORS_ES = [
  {
    name: 'Dr. Arthur Mund',
    title: 'D.D.S., Fundador',
    bio: 'El Dr. Arthur J. Mund aporta capacitación extensa y más de 40 años de experiencia en odontología cosmética y de implantes en el norte de Texas.',
    image: '/images/team/dr-mund.jpg',
  },
  {
    name: 'Dra. Olena Razo',
    title: 'D.D.S.',
    bio: 'La Dra. Olena Razo se graduó de la Escuela Dental de la Universidad de Texas en Houston. Nacida en Ucrania, llegó a Fort Worth a los 8 años y aquí ha hecho su hogar.',
    image: '/images/team/dr-razo.jpg',
  },
  {
    name: 'Dr. Omar Hiwiadi',
    title: 'D.M.D.',
    bio: 'El Dr. Omar Hiwiadi nació y creció en Fort Worth. Se especializa en odontología cosmética e implantología avanzada, incluyendo All-on-X y carillas FirstFit®.',
    image: '/images/team/dr-omar.jpg',
  },
  {
    name: 'Dr. Joshua Pham',
    title: 'D.D.S.',
    bio: 'El Dr. Joshua Pham ha vivido en el área de DFW por más de 35 años. Está certificado en LANAP® y se enfoca en odontología general y familiar.',
    image: '/images/team/dr-pham.jpg',
  },
];

// 3 blog teasers — point to most-read ES posts. Falls back gracefully if a
// post hasn't been translated yet (the /es/blog/:slug route shows an "in
// translation" notice rather than 404).
const BLOG_TEASERS_ES = [
  {
    icon: '🦷',
    title: 'Alimentos para Comer Después del Blanqueamiento Dental',
    desc: 'Qué comer y qué evitar para que tu blanqueamiento dure más tiempo.',
    link: '/es/blog/foods-to-eat-after-teeth-whitening',
  },
  {
    icon: '💎',
    title: 'Carillas First Fit — ¡NUEVO!',
    desc: 'Una sonrisa nueva en solo 2 horas con las carillas First Fit.',
    link: ROUTE_MAP_EN_TO_ES['/first-fit-veneers'],
  },
  {
    icon: '😴',
    title: 'Opciones de Sedación Dental',
    desc: 'Atención cómoda para pacientes con ansiedad dental.',
    link: '/es/blog/sedation-dentistry-fort-worth-anxious-patients',
  },
];

const OFFICE_HOURS_ES = [
  { day: 'Lunes', time: '9:00 am – 5:00 pm' },
  { day: 'Martes', time: '9:00 am – 6:00 pm' },
  { day: 'Miércoles', time: '7:00 am – 5:00 pm' },
  { day: 'Jueves', time: '9:00 am – 6:00 pm' },
  { day: 'Viernes', time: '7:00 am – 2:00 pm' },
];

const SpanishHomePage = () => {
  const { displayNumber, telLink } = useSpanishTrackingNumber();
  const [currentSlide, setCurrentSlide] = useState(0);

  // Auto-rotate every 6s (matches EN home cadence)
  useEffect(() => {
    const t = setInterval(() => {
      setCurrentSlide((i) => (i + 1) % HERO_SLIDES.length);
    }, 6000);
    return () => clearInterval(t);
  }, []);

  const goPrev = () => setCurrentSlide((i) => (i - 1 + HERO_SLIDES.length) % HERO_SLIDES.length);
  const goNext = () => setCurrentSlide((i) => (i + 1) % HERO_SLIDES.length);

  return (
    <>
      <EsSEO
        title="Dentista en Fort Worth, TX – Fort Worth Dental"
        description="Dentista familiar y cosmético en Fort Worth. Limpiezas, frenos, implantes y emergencias. Aceptamos seguro. ¡Reserva tu cita hoy!"
        canonical="/es"
        hreflangEn="/"
        ogImage={`${SITE}/logo-fwd.png`}
        schema={HOMEPAGE_SCHEMA}
      />

      {/* Hero carousel */}
      <section
        className="relative h-[420px] sm:h-[520px] lg:h-[580px] xl:h-[620px] bg-gradient-to-br from-teal-700 to-teal-900 overflow-hidden"
        data-testid="es-home-hero-carousel"
      >
        {HERO_SLIDES.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100 z-[1]' : 'opacity-0 pointer-events-none'
            }`}
            data-testid={`es-hero-slide-${index}`}
          >
            {slide.isTeamPhoto ? (
              <div className="h-full relative">
                <picture>
                  <source srcSet="/images/building.webp" type="image/webp" />
                  <img
                    src="/images/building.png"
                    alt="Edificio de Fort Worth Dental en 4620 Citylake Blvd W, Fort Worth TX"
                    className="absolute inset-0 w-full h-full object-cover object-center"
                    loading={index === 0 ? 'eager' : 'lazy'}
                    style={{ objectPosition: 'center right' }}
                  />
                </picture>
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      'linear-gradient(to right, rgba(13,94,94,0.95) 0%, rgba(13,94,94,0.85) 40%, rgba(13,94,94,0.4) 70%, transparent 100%)',
                  }}
                />
                <div className="relative h-full flex">
                  <div className="w-full lg:w-1/2 h-full flex items-center">
                    <div className="px-8 lg:px-16">
                      <div className="text-white max-w-xl">
                        <div className="mb-4 flex items-center gap-2 flex-wrap">
                          <span className="bg-yellow-500 text-yellow-900 px-3 py-1 rounded-full text-sm font-bold">
                            {slide.badge}
                          </span>
                          <span className="bg-amber-400 text-amber-900 px-3 py-1 rounded-full text-xs font-bold">
                            🇲🇽 Hablamos Español
                          </span>
                        </div>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">{slide.title}</h1>
                        <p className="text-xl md:text-2xl mb-8 text-teal-100">{slide.subtitle}</p>
                        <Link
                          to={slide.link}
                          className="bg-teal-500 hover:bg-teal-400 text-white px-8 py-3 rounded-lg text-lg font-semibold inline-block relative z-10"
                        >
                          {slide.cta}
                        </Link>
                      </div>
                    </div>
                  </div>
                  <div className="hidden lg:flex w-1/2 h-full items-end justify-center">
                    <picture>
                      <source srcSet={slide.image} type="image/webp" />
                      <img
                        src={slide.imageFallback}
                        alt="Equipo de Fort Worth Dental — Dr. Mund, Dr. Razo y staff"
                        className="max-h-[90%] w-auto object-contain drop-shadow-2xl"
                        loading="eager"
                      />
                    </picture>
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-full relative">
                <picture>
                  <source srcSet={slide.image} type="image/webp" />
                  <img
                    src={slide.imageFallback}
                    alt={`${slide.title} — Fort Worth Dental`}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />
                </picture>
                {/* Match the EN /Home.jsx neutral black gradient — using
                    teal here was tinting every non-team slide green and
                    making the whole ES carousel look darker than EN. */}
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      'linear-gradient(to right, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.1) 100%)',
                  }}
                />
                <div className="relative h-full flex items-center">
                  <div className="container mx-auto px-6 lg:px-16">
                    <div className="text-white max-w-xl">
                      <span className="inline-block bg-amber-400 text-amber-900 px-3 py-1 rounded-full text-xs font-bold mb-3">
                        🇲🇽 Hablamos Español
                      </span>
                      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">{slide.title}</h1>
                      <p className="text-lg md:text-2xl mb-7 text-teal-50">{slide.subtitle}</p>
                      <div className="flex flex-wrap gap-3">
                        <Link
                          to={slide.link}
                          className="bg-amber-500 hover:bg-amber-600 text-white px-7 py-3 rounded-lg text-lg font-semibold"
                        >
                          {slide.cta}
                        </Link>
                        <a
                          href={telLink}
                          className="bg-white/10 hover:bg-white/20 text-white border border-white/40 px-7 py-3 rounded-lg text-lg font-semibold backdrop-blur-sm"
                        >
                          📞 {displayNumber}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Slide controls */}
        <button
          onClick={goPrev}
          aria-label="Slide anterior"
          className="absolute left-4 top-1/2 -translate-y-1/2 z-[2] bg-white/30 hover:bg-white/50 text-white p-2 rounded-full backdrop-blur-sm"
          data-testid="es-hero-prev"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button
          onClick={goNext}
          aria-label="Slide siguiente"
          className="absolute right-4 top-1/2 -translate-y-1/2 z-[2] bg-white/30 hover:bg-white/50 text-white p-2 rounded-full backdrop-blur-sm"
          data-testid="es-hero-next"
        >
          <ChevronRight className="w-6 h-6" />
        </button>

        {/* Slide indicators */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[2] flex gap-2">
          {HERO_SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentSlide(i)}
              aria-label={`Ir al slide ${i + 1}`}
              className={`h-2 rounded-full transition-all ${i === currentSlide ? 'w-8 bg-amber-400' : 'w-2 bg-white/50 hover:bg-white/80'}`}
              data-testid={`es-hero-dot-${i}`}
            />
          ))}
        </div>
      </section>

      {/* Quick contact CTA strip below hero (the inline hero form moves into its own section now) */}
      <section className="bg-gradient-to-br from-amber-50 via-white to-teal-50 py-12">
        <div className="container mx-auto px-4 grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              Más de 35 años cuidando sonrisas en Fort Worth
            </h2>
            <p className="text-gray-700 mb-5 max-w-xl">
              Atendemos a familias hispanas en todo el condado de Tarrant — limpieza dental, implantes, frenos, blanqueamiento y emergencias el mismo día.
            </p>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-gray-700 max-w-md mb-5">
              <li>✓ Aceptamos seguro</li>
              <li>✓ Más de 1,100 reseñas 5★</li>
              <li>✓ Atendemos sin cita</li>
              <li>✓ Consulta gratis de implantes</li>
            </ul>
            <div className="flex flex-wrap gap-3">
              <a
                href={telLink}
                className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-6 py-3 rounded-md"
                data-testid="es-home-call"
              >
                📞 Llamar al {displayNumber}
              </a>
              <Link
                to="/es/contacto"
                className="bg-teal-600 hover:bg-teal-700 text-white font-semibold px-6 py-3 rounded-md"
                data-testid="es-home-book"
              >
                Reservar Cita
              </Link>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-6 lg:p-8 border border-gray-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Solicita tu cita</h2>
            <p className="text-sm text-gray-600 mb-4">Te llamamos en menos de 1 día hábil.</p>
            <BilingualForm lang="es" source="es_homepage_hero" />
          </div>
        </div>
      </section>

      {/* Services grid */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-2 text-center">Nuestros Servicios Dentales</h2>
          <p className="text-gray-600 text-center mb-10 max-w-2xl mx-auto">
            Todo lo que tu familia necesita bajo un mismo techo — desde limpiezas preventivas hasta odontología cosmética avanzada.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {SERVICES_FEATURED.map((s) => (
              <Link
                key={s.route}
                to={s.route}
                className="bg-white border border-gray-200 rounded-lg p-5 text-center hover:shadow-md hover:border-teal-400 transition"
                data-testid={`es-home-service-${s.route}`}
              >
                <span className="font-semibold text-gray-800">{s.es}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Trust band */}
      <section className="py-12 bg-teal-50 border-y border-teal-100">
        <div className="container mx-auto px-4 grid md:grid-cols-3 gap-6 text-center">
          <div>
            <p className="text-3xl font-bold text-teal-700">1,100+</p>
            <p className="text-gray-700">Reseñas de 5 estrellas en Google</p>
          </div>
          <div>
            <p className="text-3xl font-bold text-teal-700">35+</p>
            <p className="text-gray-700">Años atendiendo a Fort Worth</p>
          </div>
          <div>
            <p className="text-3xl font-bold text-teal-700">Best of 2025</p>
            <p className="text-gray-700">Reconocidos como mejor dentista de Fort Worth</p>
          </div>
        </div>
      </section>

      {/* New Patient Welcome Offers */}
      <section className="py-16 bg-white" data-testid="es-home-welcome-offers">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-teal-600 font-semibold text-lg mb-2">Los Mejores Dentistas de Fort Worth — Aceptando Nuevos Pacientes</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-800">Ofertas de Bienvenida para Pacientes Nuevos</h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              Descubre por qué Fort Worth Dental es hogar de los mejores dentistas de Fort Worth. Nuestro equipo galardonado ofrece atención dental excepcional para toda la familia.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {WELCOME_OFFERS_ES.map((offer, index) => (
              <article
                key={index}
                className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-xl transition-shadow"
                data-testid={`es-home-offer-${index}`}
              >
                <div className="h-48 overflow-hidden bg-gray-100">
                  <img
                    src={offer.image}
                    alt={offer.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-800 mb-3 line-clamp-2">{offer.title}</h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">{offer.description}</p>
                  <Link
                    to={offer.link}
                    className="inline-block bg-teal-600 hover:bg-teal-700 text-white font-semibold px-5 py-2.5 rounded-md text-sm"
                  >
                    {offer.cta}
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* LANAP / Implants dual callout */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <Link to={ROUTE_MAP_EN_TO_ES['/lanap']} className="block group" data-testid="es-home-lanap-card">
              <div className="relative h-72 sm:h-80 rounded-lg overflow-hidden shadow-lg">
                <img
                  src="/images/services/dentist-examining.jpg"
                  alt="Tratamiento láser para enfermedad de las encías LANAP — Fort Worth Dental"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <h3 className="text-white text-xl sm:text-2xl font-bold text-center px-4">
                    ¿Soy Candidato para el Tratamiento Láser de Encías LANAP®?
                  </h3>
                </div>
              </div>
            </Link>
            <Link to={ROUTE_MAP_EN_TO_ES['/dental-implants']} className="block group" data-testid="es-home-implants-card">
              <div className="relative h-72 sm:h-80 rounded-lg overflow-hidden shadow-lg">
                <img
                  src="/images/services/dentist-examines.jpg"
                  alt="Implantes Dentales en Fort Worth — Dr. Mund consultando con paciente"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <h3 className="text-white text-xl sm:text-2xl font-bold text-center px-4">
                    ¿Son los Implantes Dentales para Mí?
                  </h3>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Convenient Dental Services */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div>
              <img
                src="/images/services/frame-1293-2.png"
                alt="Servicios dentales convenientes en Fort Worth Dental — horarios flexibles"
                className="rounded-lg w-full"
                loading="lazy"
                width={600}
                height={400}
              />
            </div>
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-6">Servicios Dentales Convenientes</h2>
              <p className="text-gray-700 text-lg leading-relaxed">
                ¡Deja que nuestro equipo se ajuste a tu horario, no al revés! Ofrecemos varios horarios flexibles cada mes para pacientes con vidas ocupadas: citas en la mañana, tarde y noche.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* We Accept Your Dental Insurance */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-6">Aceptamos Tu Seguro Dental</h2>
              <p className="text-gray-700 text-lg leading-relaxed">
                Queremos que pagar tu atención dental sea lo más sencillo posible. Por eso Fort Worth Dental está dentro de la red de una amplia variedad de planes de seguro dental. Nuestro equipo te explicará tus beneficios y procesará todos los trámites por ti.
              </p>
            </div>
            <div>
              <img
                src="/images/services/frame-1293.png"
                alt="Aceptamos seguro dental en Fort Worth Dental — la mayoría de planes PPO y HMO"
                className="rounded-lg w-full"
                loading="lazy"
                width={600}
                height={400}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Meet Our Team */}
      <section className="py-16 bg-gray-50" data-testid="es-home-doctors">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-teal-600 font-semibold text-lg mb-2">Conoce al Equipo de Fort Worth Dental</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-4">
              Bienvenido a la Odontología Familiar de Fort Worth
            </h2>
            <p className="text-gray-600 max-w-4xl mx-auto">
              Bienvenido a nuestra clínica familiar de última generación. Nuestro equipo y doctores se dedican a mejorar la salud y apariencia de las sonrisas en Fort Worth, Aledo, y áreas cercanas. Somos una práctica integral con servicios desde cuidado preventivo básico hasta tratamientos cosméticos como blanqueamiento y restaurativos como implantes, carillas, Invisalign y LANAP.
            </p>
            <Link to={ROUTE_MAP_EN_TO_ES['/our-team']} className="inline-block mt-6 bg-teal-600 hover:bg-teal-700 text-white px-8 py-3 rounded-lg font-semibold">
              Conoce al Equipo
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {DOCTORS_ES.map((doctor) => (
              <div key={doctor.name} className="bg-white rounded-lg shadow hover:shadow-xl transition-shadow p-6 text-center">
                <div className="mb-4 flex justify-center">
                  <div className="relative w-44 h-52 rounded-full overflow-hidden border-4 border-teal-200">
                    <img
                      src={doctor.image}
                      alt={`${doctor.name} - ${doctor.title} en Fort Worth Dental`}
                      className="w-full h-full object-cover object-top"
                      style={{ objectPosition: 'center 20%' }}
                      loading="lazy"
                    />
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-1">{doctor.name}</h3>
                <p className="text-teal-600 font-medium mb-3 text-sm">{doctor.title}</p>
                <p className="text-gray-600 text-sm line-clamp-4">{doctor.bio}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <img
              src="/images/team/team-photo.jpg"
              alt="Foto del equipo de Fort Worth Dental — Dr. Mund, Dra. Razo, Dr. Hiwiadi, Dr. Pham e higienistas dentales"
              className="rounded-lg shadow-xl mx-auto max-w-4xl w-full"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Google Reviews — widget is language-agnostic; reviews come direct from Google */}
      <section className="py-16 bg-white" data-testid="es-home-reviews">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-teal-600 font-semibold text-lg mb-2">Lo Que Dicen Nuestros Pacientes</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-800">Reseñas de Pacientes</h2>
          </div>
          <div className="max-w-xl mx-auto">
            <GoogleReviewsWidget variant="compact" />
          </div>
          <div className="text-center mt-8">
            <Link
              to="/es/testimonios"
              className="inline-block bg-teal-600 hover:bg-teal-700 text-white px-8 py-3 rounded-lg font-semibold transition"
            >
              Ver Todos los Testimonios
            </Link>
          </div>
        </div>
      </section>

      {/* Insurance carousel — logos only, language-agnostic */}
      <InsuranceCarousel lang="es" />

      {/* Practice videos */}
      <section className="py-16 bg-white" data-testid="es-home-videos">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-800 mb-2 text-center">Conoce Nuestra Clínica en Acción</h2>
          <p className="text-gray-600 text-center mb-8">Mira videos de Fort Worth Dental</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <div className="bg-gray-50 rounded-lg overflow-hidden shadow-md">
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  title="Videos de Fort Worth Dental"
                  src="https://www.youtube.com/embed/videoseries?list=UUUFwAVnty9mZPxUYT9VLyIA"
                  className="absolute inset-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
              <div className="p-4">
                <h3 className="font-bold text-gray-800">Videos de Fort Worth Dental</h3>
                <p className="text-sm text-gray-600 mt-1">Últimas novedades de nuestra clínica</p>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg overflow-hidden shadow-md">
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  title="El Dr. Arthur Mund explica los implantes dentales"
                  src="https://www.youtube.com/embed/sozRchIFTFw"
                  className="absolute inset-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
              <div className="p-4">
                <h3 className="font-bold text-gray-800">Implantes Dentales Explicados</h3>
                <p className="text-sm text-gray-600 mt-1">El Dr. Arthur Mund le explica sus opciones</p>
              </div>
            </div>
          </div>
          <div className="text-center mt-6">
            <a
              href="https://www.youtube.com/channel/UCUFwAVnty9mZPxUYT9VLyIA"
              target="_blank"
              rel="noopener noreferrer"
              className="text-teal-600 font-semibold hover:text-teal-800"
            >
              Visita Nuestro Canal de YouTube →
            </a>
          </div>
        </div>
      </section>

      {/* CTA banner */}
      <section className="py-16 bg-teal-600 text-white" data-testid="es-home-cta-banner">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">¿Listo para la Mejor Atención Dental de Fort Worth?</h2>
          <p className="text-xl mb-8">¡Agenda tu consulta GRATIS hoy y descubre por qué más de 1,100 pacientes nos califican con 5 estrellas!</p>
          <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-6">
            <a
              href={telLink}
              className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100 inline-block"
            >
              Llama a {displayNumber}
            </a>
            <Link
              to="/es/contacto"
              className="bg-yellow-500 text-yellow-900 px-8 py-4 rounded-lg text-lg font-bold hover:bg-yellow-400 inline-block"
            >
              Agendar Consulta GRATIS
            </Link>
          </div>
        </div>
      </section>

      {/* Dental Tips & News */}
      <section className="py-16 bg-white" data-testid="es-home-blog-teasers">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-800 mb-2 text-center">Consejos y Noticias Dentales</h2>
          <p className="text-gray-600 text-center mb-8">Consejos expertos de tu equipo dental en Fort Worth</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {BLOG_TEASERS_ES.map((post) => (
              <Link
                key={post.title}
                to={post.link}
                className="bg-gray-50 p-6 rounded-lg hover:shadow-lg transition group block"
              >
                <span className="text-3xl mb-3 block" aria-hidden="true">{post.icon}</span>
                <h3 className="font-bold text-gray-800 mb-2 group-hover:text-teal-600">{post.title}</h3>
                <p className="text-gray-600 text-sm">{post.desc}</p>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/es/blog" className="text-teal-600 font-semibold hover:text-teal-800">
              Ver Todas las Noticias y Consejos Dentales →
            </Link>
          </div>
        </div>
      </section>

      {/* Office hours + booking form */}
      <section className="py-16 bg-gray-50" data-testid="es-home-office-hours">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-gray-800 mb-6">Horario de Atención</h2>
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-teal-600 text-white">
                    <tr>
                      <th className="py-4 px-6 text-left">Día</th>
                      <th className="py-4 px-6 text-left">Horario</th>
                    </tr>
                  </thead>
                  <tbody>
                    {OFFICE_HOURS_ES.map((row, i) => (
                      <tr key={row.day} className={i < OFFICE_HOURS_ES.length - 1 ? 'border-b' : ''}>
                        <td className="py-3 px-6">{row.day}</td>
                        <td className="py-3 px-6">{row.time}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-sm text-gray-600 mt-4">
                ¿Emergencia dental fuera del horario?{' '}
                <a href={telLink} className="text-teal-700 font-semibold hover:underline">
                  Llámanos al {displayNumber}
                </a>
              </p>
            </div>
            <div>
              <div className="bg-white rounded-2xl shadow-xl p-6 lg:p-8 border border-gray-100">
                <h2 className="text-2xl font-bold text-gray-900 mb-1">Reserva tu Cita</h2>
                <p className="text-sm text-gray-600 mb-4">Te llamamos en menos de 1 día hábil.</p>
                <BilingualForm lang="es" source="es_homepage_footer" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default SpanishHomePage;
